<template>
  <communication-log-card
    background-class="bg-secondary-purple-1"
    :comments="log.data.comments"
  >
    <template #icon>
      <icon-base
        :icon="IconActivityHub"
        height="22"
        width="22"
        class="-mt-1 mr-1 text-white"
      />
    </template>
    <template #content>
      <div class="flex flex-col space-y-2 border-b text-gray-500">
        <div class="rounded-t-md bg-grey-hover px-4 py-2 font-medium">
          <span
            class="cursor-pointer"
            data-cy="task-deal-link"
            @click="openDeal"
          >
            {{
              log.data.taskable?.business?.business_legal_name ??
              $t("COMMON.DEAL_LINK")
            }}
          </span>
        </div>
        <div class="task-meta-wrapper px-4 py-2">
          <div class="wrap flex justify-between">
            <div class="meta flex items-center space-x-2">
              <lf-user-selection
                v-tooltip="{
                  content: $t('ACTIVITY.TASK.ASSIGNEE', {
                    name: formatUserFullName(log.data.assignee),
                  }),
                  onShow: () => log.data.assignee,
                }"
                name="assignee"
                :value="log.data?.assignee"
                :initial-user-selection="log.data?.assignee"
                :get-paginated-options="getUsersByRole"
                :disabled="!isEditing"
                avatar-only-trigger-button
                value-type="user"
                data-cy="assignee"
              />
              <task-priority-dropdown
                :priority-level="log.data.priority_level"
                :disabled="!isEditing"
                data-cy="priority"
              />

              <task-due-date :date="log.data.due_at" :disabled="!isEditing" />
            </div>
            <div class="actions flex items-center space-x-2">
              <task-status-dropdown
                :task-status="log.data.status"
                :is-loading="statusLoading"
                data-cy="task-status"
                @change="statusChange"
              />
              <loader :is-loading="deleteLoading" />
              <action-menu-wrapper
                v-if="!isEditing && !shouldReloadMenu"
                :items="menuOptions"
                :offset="offset"
                teleport
                data-cy="action-menu"
              />
            </div>
          </div>
        </div>
        <task-description
          :description="log.data.body"
          :disabled="!isEditing"
          data-cy="description"
        />
      </div>
    </template>
    <template #footer>
      <div v-if="log" class="flex w-full justify-between space-y-1 bg-white">
        <div class="flex-row-full-justify-between wrapper">
          <div class="wrap-children flex items-center space-x-2">
            <span class="text-xs font-medium">
              {{ $t("ACTIVITY.TASK.FROM") }}
            </span>
            <avatar-with-name
              v-if="log.data.user"
              :user="log.data.user"
              data-cy="user-name"
            />
            <span v-if="log.data?.created_at">&#8226;</span>
            <date-time :date="log.data?.created_at" />
          </div>
          <modal-buttons
            v-if="isEditing"
            class="bg-white"
            :on-cancel="handleCancel"
            :on-save="submit"
            no-vertical-padding
          />
        </div>
      </div>
    </template>
  </communication-log-card>
</template>
<script lang="ts" setup>
import { type PropType, computed, nextTick, provide, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import isEmpty from "lodash/isEmpty";
import { storeToRefs } from "pinia";
import { useForm } from "vee-validate";

import { LogEndpointType, type TaskStatuses } from "@/enums/communicationLogs";
import { COMMON_MENU_OFFSET } from "@/helpers/constants";
import { formatUserFullName } from "@/helpers/formatting";
import { CommunicationLogContextKey } from "@/helpers/symbols";
import { useAuth } from "@/hooks/auth";
import { useClients } from "@/hooks/clients";
import { usePromiseWrapper } from "@/hooks/common";
import { ROUTE_DEAL_PROGRESS } from "@/router/routes";
import usersApiService from "@/services/modules/users";
import { useCommunicationStore } from "@/stores/communication";

import CommunicationLogCard from "@/components/communicationLogs/communication/CommunicationLogCard.vue";
import TaskDescription from "@/components/communicationLogs/forms/TaskDescription.vue";
import TaskDueDate from "@/components/communicationLogs/forms/TaskDueDate.vue";
import TaskPriorityDropdown from "@/components/communicationLogs/forms/TaskPriorityDropdown.vue";
import TaskStatusDropdown from "@/components/communicationLogs/forms/TaskStatusDropdown.vue";
import IconActivityHub from "@/components/icons/IconActivityHub.vue";
import ActionMenuWrapper, {
  type MenuItem,
} from "@/components/ui/ActionMenuWrapperV2.vue";
import AvatarWithName from "@/components/ui/AvatarWithName.vue";
import DateTime from "@/components/ui/ConvertibleDateTime.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import LfUserSelection from "@/components/ui/inputs/LfUserSelection.vue";

import type { TaskLog, TaskLogData } from "@/models/communicationLogs";

const props = defineProps({
  log: {
    type: Object as PropType<TaskLog>,
    required: true,
  },
});

const offset = COMMON_MENU_OFFSET;

provide(CommunicationLogContextKey, {
  type: LogEndpointType.Task,
  logId: props.log.data.id,
});

const router = useRouter();
const { t } = useI18n();
const auth = useAuth();
const { authClientSettings: client } = useClients();
const { updateTask, deleteTask: removeTask } = useCommunicationStore();
const { logs } = storeToRefs(useCommunicationStore());

const isEditing = ref(false);
const shouldReloadMenu = ref(false);

const dealRoute = computed(() =>
  router.resolve({
    name: ROUTE_DEAL_PROGRESS,
    params: { id: props.log.data?.taskable.application_id?.toString() },
  })
);

const initialFormValues = computed(() => ({
  assignee: props.log.data.assignee,
  priority_level: props.log.data.priority_level,
  due_at: props.log.data.due_at,
  status: props.log.data.status,
  description: props.log.data.body,
}));

const { handleSubmit, resetForm } = useForm({
  initialValues: initialFormValues.value,
});

const submit = handleSubmit(async (values: Partial<TaskLogData>) => {
  const payload = {
    ...values,
    assignee_id: values.assignee?.id,
  };
  if (payload.due_at) {
    payload.due_at = new Date(payload.due_at).toISOString();
  }
  delete payload.assignee;
  await updateTask(props.log.data.id, payload, values.assignee);
  toggleEditTaskMode();
});

const openDeal = () => {
  window.open(dealRoute.value.href, "_blank");
};

const toggleEditTaskMode = () => {
  isEditing.value = !isEditing.value;
};

const { loading: deleteLoading, fetchWrapper: deleteTask } = usePromiseWrapper(
  async () => {
    await removeTask(props.log.data.id);
  }
);

const menuOptions = computed(() => {
  const options: MenuItem[] = [
    {
      label: t("COMMON.EDIT"),
      handler: toggleEditTaskMode,
    },
  ];

  if (auth.isAdmin) {
    options.push({
      label: t("COMMON.DELETE"),
      handler: deleteTask,
      class: "text-red-500",
    });
  }
  return options;
});

const getUsersByRole = (params: Record<string, string | number> = {}) => {
  const isAClient = !isEmpty(client.value);
  if (isAClient) {
    return usersApiService.getClientUsers(client.value.id, {
      ...params,
    });
  }
  return usersApiService.getEmployees({ ...params });
};

const statusChange = async (status: TaskStatuses) => {
  if (isEditing.value) {
    return;
  }
  await handleStatusChange(status);
};

const { loading: statusLoading, fetchWrapper: handleStatusChange } =
  usePromiseWrapper(async (status: TaskStatuses) => {
    await updateTask(props.log.data.id, { status });
  });

const handleCancel = () => {
  resetForm({ values: initialFormValues.value });
  toggleEditTaskMode();
};

watch(logs, (updatedLogs, oldLogs) => {
  if (updatedLogs.length < oldLogs.length) {
    shouldReloadMenu.value = true;
    nextTick(() => {
      shouldReloadMenu.value = false;
    });
  }
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.task-meta-wrapper {
  container-type: inline-size;
  container-name: task-meta-wrapper;
}

@container task-meta-wrapper (max-width: 500px) {
  :deep() {
    .meta {
      @apply -ml-1 flex-wrap space-y-1;
    }
    .actions {
      @apply w-1/2 justify-end;
    }
  }
}

@container task-meta-wrapper (max-width: 375px) {
  :deep() {
    .wrap {
      @apply flex-wrap;
    }
    .meta,
    .actions {
      @apply w-full;
    }
  }
}
</style>
